/* eslint @typescript-eslint/no-explicit-any:0 */
import React, { createContext, ReactElement, useMemo } from 'react'
import { findOneByName } from '@s4l/utils/library/react'
import { getDirection } from '@s4l/utils/library/rtl'
import debug from 'debug'

import type { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel, { type EmblaViewportRefType } from 'embla-carousel-react'

import { Content, IPropsContent } from './Content'
import styles from './styles.module.css'
import { Buttons, IButtonsProps } from './Buttons'
import { IPaginationProps, Pagination } from './Pagination'
import { useCarousel } from './hooks/useCarousel'
import { Dots, IDotsProps } from './Dots'

const log = debug('s4l:ds:Carousel')

export type ComponentWithDisplayName = React.ComponentType<any> & { displayName?: string }
export type DirectionKind = 'prev' | 'next'
export type Preset = 'single-items' | 'multiple-items'
export type NavigationCallback = (direction: DirectionKind) => void
export type IProps = {
  preset: Preset
  children: React.ReactNode
  onNavigation?: NavigationCallback
}

const OPTIONS_BY_PRESET: { [key in Preset]: EmblaOptionsType } = {
  'multiple-items': { loop: false, slidesToScroll: 'auto', align: 'start' },
  'single-items': { loop: false },
}

export type CarouselContextType = {
  itemsPerPage: [number, number, number]
  totalItems: number
  preset: Preset
  onNavigation?: (callback: NavigationCallback) => void
  notifyNavigation?: NavigationCallback
}

export const CarouselContext = createContext<CarouselContextType>({
  itemsPerPage: [0, 0, 0],
  totalItems: 0,
  preset: 'multiple-items',
})

const Carousel = ({ children, preset, onNavigation: onNavigationProps }: IProps) => {
  const contentNode = findOneByName(children, 'Carousel.Content') as ReactElement

  const startIndex = contentNode?.props?.initialItemIndex ?? 0
  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...OPTIONS_BY_PRESET[preset],
    startIndex,
    direction: getDirection(),
    watchDrag: (emblaApiProp, evt) => {
      const target = evt.target as HTMLElement
      const currentViewport = emblaApiProp.rootNode()
      const targetEmblaViewport = target.closest('[data-embla-viewport]')

      return targetEmblaViewport === currentViewport
    },
  })

  const { onHandleNavigation, onNavigation } = useCarousel(onNavigationProps)

  const contextValue = useMemo(
    () => ({
      itemsPerPage: contentNode?.props.itemsPerPage || [1, 1, 1],
      totalItems: React.Children.count(contentNode?.props.children),
      preset,
      onNavigation,
    }),
    [contentNode?.props.itemsPerPage, contentNode?.props.children, preset, onNavigation]
  )

  if (!contentNode) {
    log('Carousel.Content is required')
    return null
  }
  if (React.Children.count(contentNode.props.children) === 0) {
    log('Carousel must have at least one child')
    return null
  }

  // Add Content component validations here too since they affect the whole Carousel
  if (preset === 'single-items' && contentNode.props.itemsPerPage !== undefined) {
    log('itemsPerPage must be undefined when preset is single-items')
    return null
  }
  if (preset === 'multiple-items' && contentNode.props.itemsPerPage === undefined) {
    log('itemsPerPage must be defined when preset is multiple-items')
    return null
  }
  if (preset === 'multiple-items' && contentNode.props.initialItemIndex !== undefined) {
    log('initialItemIndex must be undefined when preset is multiple-items')
    return null
  }

  // Check Buttons validation too since it affects the whole Carousel
  const buttonsNode = findOneByName(children, 'Carousel.Buttons')
  if (
    buttonsNode &&
    React.isValidElement(buttonsNode) &&
    React.Children.count(buttonsNode.props.children) !== 2
  ) {
    log('children count must be 2')
    return null
  }

  const nextChildren = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child

    const childType = child?.type as ComponentWithDisplayName

    if (childType.displayName === 'Carousel.Content') return React.cloneElement(child as ReactElement<IPropsContent & {ref:EmblaViewportRefType }>, { ref: emblaRef, preset }) // prettier-ignore
    if (childType.displayName === 'Carousel.Buttons') return React.cloneElement(child as ReactElement<IButtonsProps>, { emblaApi, onHandleNavigation }) // prettier-ignore
    if (childType.displayName === 'Carousel.Pagination') return React.cloneElement(child as ReactElement<IPaginationProps>, { startIndex, emblaApi }) // prettier-ignore
    if (childType.displayName === 'Carousel.Dots') return React.cloneElement(child as ReactElement<IDotsProps>, { startIndex, emblaApi }) // prettier-ignore

    return child
  })

  return (
    <CarouselContext.Provider value={contextValue}>
      <section className={styles.embla} data-testid="carousel">
        {nextChildren}
      </section>
    </CarouselContext.Provider>
  )
}

Carousel.Content = Content
Carousel.Buttons = Buttons
Carousel.Pagination = Pagination
Carousel.Dots = Dots

export default Carousel
